import { defineStore } from "pinia";
import { ApiClient } from "@/api-client";
import {
  useSystemStore,
  useApplicationStore,
  useSupplierStore,
  useMaterialStore,
} from "./index";
import * as ApiResponse from "@/api-client/responses";
import { ref } from "vue";

const apiClient = ApiClient.getInstance();

export const useLabelStore = defineStore("labelStore", {
  state: () => ({
    labels: [] as ApiResponse.PrintItems.GetPrintItems,
    loadedPreviews: new Map<
      string,
      {
        loadingState: loadingStateType;
        url: string;
      }
    >(),
    loadingState: "unset" as loadingStateType,
    abortController: new AbortController(),
    failedReason: "",
    indicateTimeout: false,
    newLabelCountBeforeHistoryChecked: 0,
  }),
  actions: {
    abortGetLabels() {
      this.abortController.abort();
      this.abortController = new AbortController();
      this.labels = [];
    },
    async getLabels() {
      // get needed stores instances
      const systemStore = useSystemStore();
      const applicationStore = useApplicationStore();
      const supplierStore = useSupplierStore();
      const materialStore = useMaterialStore();

      // clear old data
      this.labels = [];

      // check if system and supplier and material was set
      if (
        !supplierStore.selectedSupplier ||
        !systemStore.selectedSystem ||
        !materialStore.selectedMatNr
      ) {
        applicationStore.addAlert({
          type: "Alert",
          key: "app.printLabels.loadingData.error",
          keyData: {
            message: "No system, supplier or material was selected",
          },
        });
        return;
      }

      // set state and get new data
      this.loadingState = "loading";
      try {
        this.labels = await apiClient.getLabelList(
          materialStore.selectedMatNr,
          supplierStore.selectedSupplier.SupplierId,
          systemStore.selectedSystem.supplierId,
          systemStore.selectedSystem.system,
          this.abortController.signal
        );
      } catch (error) {
        if (error instanceof Error && error?.name == "AbortError") return;
        // handle error
        applicationStore.addAlert({
          type: "Alert",
          key: "app.printLabels.loadingData.error",
          keyData: {
            message: error instanceof Error ? error.message : String(error),
          },
        });
        this.loadingState = "failed";
        return;
      }
      // set done state
      this.loadingState = "success";

      materialStore.selectedMatNrDescription =
        getMaterialDescriptionFromLabelData(
          this.labels.map((e) => String(e.PrintData))
        );
    },
    async loadResourceLocationFromLabel(
      label: ApiResponse.PrintItems.PrintItem
    ) {
      // get needed stores
      const systemStore = useSystemStore();
      const applicationStore = useApplicationStore();
      const supplierStore = useSupplierStore();
      const materialStore = useMaterialStore();

      // key
      const key = JSON.stringify(label);

      // check if not already loaded
      const foundEntry = this.loadedPreviews.get(key);

      if (
        foundEntry &&
        (foundEntry.loadingState === "success" ||
          foundEntry.loadingState === "loading")
      )
        return;

      // check if user has made selections
      if (
        !supplierStore.selectedSupplier ||
        !systemStore.selectedSystem ||
        !materialStore.selectedMatNr
      ) {
        applicationStore.addAlert({
          type: "Alert",
          key: "app.printLabels.loadingPreview.error",
          keyData: {
            message: "No system, supplier or material was selected",
          },
        });
        throw new Error("No system, supplier or material was selected");
      }

      // set state
      this.loadedPreviews.set(key, {
        loadingState: "loading",
        url: "",
      });

      // get preview image
      let blob;
      try {
        blob = await apiClient.getPreviewImage(
          label.MaterialId,
          supplierStore.selectedSupplier.SupplierId,
          systemStore.selectedSystem.supplierId,
          systemStore.selectedSystem.system,
          label.Layout,
          label.PrintData ?? ""
        );
      } catch (error) {
        // set state
        this.loadedPreviews.set(key, {
          loadingState: "failed",
          url: "",
        });
        throw error;
      }

      // convert blob to accessible url
      const loadedPreviewUrl = window.URL.createObjectURL(blob);
      // add loaded url to list
      this.loadedPreviews.set(key, {
        loadingState: "success",
        url: loadedPreviewUrl,
      });
      return loadedPreviewUrl;
    },
    async downloadLabel(params: {
      label: ApiResponse.PrintItems.PrintItem;
      quantity: number;
    }) {
      // get needed stores instances
      const systemStore = useSystemStore();
      const applicationStore = useApplicationStore();
      const supplierStore = useSupplierStore();
      const materialStore = useMaterialStore();

      // check if system and supplier and material was set
      if (
        !supplierStore.selectedSupplier ||
        !systemStore.selectedSystem ||
        !materialStore.selectedMatNr
      ) {
        applicationStore.addAlert({
          type: "Alert",
          key: "app.printLabels.loadingData.error",
          keyData: {
            message: "No system, supplier or material was selected",
          },
        });
        return;
      }

      const labelDate = new Date();

      try {
        await apiClient.downloadGeneratedLabel(
          materialStore.selectedMatNr,
          supplierStore.selectedSupplier.SupplierId,
          String(params.quantity),
          params.label.Layout,
          params.label.PrintData ?? "",
          systemStore.selectedSystem.supplierId,
          systemStore.selectedSystem.system,
          `${labelDate.getFullYear()}.${(labelDate.getMonth() + 1)
            .toString()
            .padStart(2, "0")}.${labelDate
            .getDate()
            .toString()
            .padStart(2, "0")}`
        );
      } catch (error) {
        this.indicateTimeout = true;
        throw error;
      }

      this.newLabelCountBeforeHistoryChecked++;
    },
  },
});

function getMaterialDescriptionFromLabelData(printDataList: string[]): {
  de: string;
  en: string;
} {
  let de = "",
    en = "";

  for (const printData of printDataList) {
    if (!printData) continue;
    // split at ';'
    const splittedPrintData = printData.split(";");
    for (const printDataEntry of splittedPrintData) {
      if (printDataEntry.startsWith("MAKTX=")) de = printDataEntry.substring(6);

      if (printDataEntry.startsWith("MAKTX_E="))
        en = printDataEntry.substring(8);
    }
  }

  return { de, en };
  //
}
